import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../AuthContext';
import { usePayers } from '../../../../context/PayersContext';
import {
    Typography,
    Button,
    Input,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter
} from '@material-tailwind/react';
import ReactSelect from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import SerialNumbersField, { serialCountError } from '../../parts/SerialNumbersField';
const { rootUrl } = require('../../../../config/config');

const PartDialog = ({ open, editingPart, jobCardId, serviceLocation, onClose, onPartAdded }) => {
    const { token, user } = useAuth();
    const { payers, loading: payersLoading } = usePayers();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [availableParts, setAvailableParts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [manufacturers, setManufacturers] = useState([]);
    const [vatRates, setVatRates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isCreatingNewPart, setIsCreatingNewPart] = useState(false);
    const [errors, setErrors] = useState({});
    const [banner, setBanner] = useState(null); // { type: 'success' | 'error', text: string }

    const [formData, setFormData] = useState({
        part_id: '',
        quantity_used: 1,
        unit_cost: 0,
        discount_amount: 0,
        paid_by: 'OFFICE',
        is_sale: false,
        notes: '',
        serials: []
    });

    const [newPartData, setNewPartData] = useState({
        part_name: '',
        part_number: '',
        manufacturer: '',
        category: '',
        vat_id: 1,
        cost_excl: 0,
        cost_incl: 0,
        price_excl: 0,
        price_incl: 0,
        quantity: 0
    });

    // VAT helpers
    const getVatRateById = (id) => {
        const v = vatRates.find(v => Number(v.vat_id) === Number(id));
        return v ? Number(v.vat_rate) : 0;
    };

    const toFixed2 = (n) => {
        const num = Number(n);
        if (!isFinite(num)) return '';
        return (Math.round(num * 100) / 100).toFixed(2);
    };

    const computeIncl = (excl, rate) => {
        if (excl === '' || excl === null || excl === undefined) return '';
        return toFixed2(Number(excl) * (1 + (Number(rate) || 0) / 100));
    };

    const computeExcl = (incl, rate) => {
        if (incl === '' || incl === null || incl === undefined) return '';
        return toFixed2(Number(incl) / (1 + (Number(rate) || 0) / 100));
    };

    const formatCurrency = (amount) => {
        if (!amount) return '0.00';
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(amount);
    };

    // Fetch reference data when dialog opens
    useEffect(() => {
        if (open) {
            fetchAvailableParts();
            fetchCategories();
            fetchManufacturers();
            fetchVatRates();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Populate form when editing
    useEffect(() => {
        if (editingPart) {
            setFormData({
                part_id: editingPart.part_id,
                quantity_used: editingPart.quantity_used,
                unit_cost: editingPart.unit_cost,
                discount_amount: editingPart.discount_amount || 0,
                paid_by: editingPart.paid_by || 'OFFICE',
                is_sale: editingPart.is_sale || false,
                notes: editingPart.notes || '',
                serials: editingPart.serials || []
            });
        }
    }, [editingPart]);

    const fetchAvailableParts = async () => {
        try {
            const response = await axios.get(
                `${rootUrl}/car_parts`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );
            // Support both raw array and { success, data }
            const parts = Array.isArray(response.data) ? response.data : (response.data?.data || []);
            setAvailableParts(parts);
        } catch (error) {
            console.error('Error fetching available parts:', error);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(
                `${rootUrl}/parts/categories`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );
            setCategories(response.data || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchManufacturers = async () => {
        try {
            const response = await axios.get(
                `${rootUrl}/parts/manufacturers`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );
            setManufacturers(response.data || []);
        } catch (error) {
            console.error('Error fetching manufacturers:', error);
        }
    };

    const fetchVatRates = async () => {
        try {
            const response = await axios.get(
                `${rootUrl}/system/vat-rates`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );
            setVatRates(response.data || []);
        } catch (error) {
            console.error('Error fetching VAT rates:', error);
        }
    };

    const validateNewPart = (data) => {
        const newErrors = {};
        if (!data.part_name?.trim()) newErrors.part_name = 'Part name is required';
        if (!data.part_number?.trim()) newErrors.part_number = 'Part number is required';
        if (!data.manufacturer?.trim()) newErrors.manufacturer = 'Manufacturer is required';
        if (!data.category?.trim()) newErrors.category = 'Category is required';
        if (isNaN(data.cost_excl) || data.cost_excl < 0) newErrors.cost_excl = 'Valid cost (excl. VAT) is required';
        if (isNaN(data.cost_incl) || data.cost_incl < 0) newErrors.cost_incl = 'Valid cost (incl. VAT) is required';
        if (isNaN(data.price_excl) || data.price_excl <= 0) newErrors.price_excl = 'Price (excl. VAT) must be greater than 0';
        if (isNaN(data.price_incl) || data.price_incl <= 0) newErrors.price_incl = 'Price (incl. VAT) must be greater than 0';
        if (isNaN(data.quantity) || data.quantity < 0) newErrors.quantity = 'Valid quantity is required';
        return newErrors;
    };

    const handleCreateNewPart = async () => {
        const newErrors = validateNewPart(newPartData);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(
                `${rootUrl}/car_parts`,
                {
                    ...newPartData,
                    input_by: user?.username || 'system'
                },
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            // Consider multiple success shapes
            const payloadRaw = response.data;
            const isSuccess = (payloadRaw?.success === true) || !!payloadRaw?.part_id || !!payloadRaw?.part?.part_id || (!!payloadRaw?.data && (payloadRaw.data.part_id || payloadRaw.data.part?.part_id));

            if (isSuccess) {
                const payload = payloadRaw?.data ? payloadRaw.data : payloadRaw;
                const insertedPart = payload.part ? payload.part : payload;
                const insertedPartId = payload.part_id ? payload.part_id : insertedPart.part_id;

                await fetchAvailableParts();
                setFormData({
                    part_id: insertedPartId,
                    quantity_used: 1,
                    unit_cost: insertedPart.price_incl || 0,
                    discount_amount: 0,
                    paid_by: 'OFFICE',
                    is_sale: false,
                    notes: ''
                });

                setIsCreatingNewPart(false);
                resetNewPartData();
                setErrors({});
                const successMsg = payloadRaw?.message || 'Car part added successfully';
                setBanner({ type: 'success', text: successMsg });
            } else {
                const errMsg = payloadRaw?.message || 'Failed to create part';
                setErrors({ submit: errMsg });
                setBanner({ type: 'error', text: errMsg });
            }
        } catch (error) {
            console.error('Error creating part:', error);
            const errMsg = error.response?.data?.message || 'Failed to create part';
            setErrors({ submit: errMsg });
            setBanner({ type: 'error', text: errMsg });
        } finally {
            setLoading(false);
        }
    };

    const selectedPart = availableParts.find(p => p.part_id === formData.part_id);
    const selectedPartIsSerial = selectedPart?.has_serial === true;
    // Serial tracking switched on before every unit in stock had a serial: blocked until they're added
    const selectedPartSerialsMissing = selectedPartIsSerial && selectedPart?.serials_pending === true;
    const serialsMissingMessage = `${selectedPart?.part_name || 'This part'} is blocked until serial numbers are added for the units already in stock. Open the part in Parts, go to its Serial Numbers tab and use "Add missing serials".`;

    // Serial-tracked parts need one serial per unit; sets the field error and returns false when invalid
    const validateSerials = () => {
        if (!selectedPartIsSerial) return true;
        // Lines added before serial tracking was enabled can be edited without serials if quantity is unchanged
        const legacyUntouched = editingPart && (editingPart.serials || []).length === 0
            && Number(formData.quantity_used) === Number(editingPart.quantity_used) && formData.serials.length === 0;
        if (legacyUntouched) return true;
        if (selectedPartSerialsMissing) {
            setErrors(prev => ({ ...prev, serials: serialsMissingMessage }));
            return false;
        }
        const serialError = serialCountError(formData.serials, formData.quantity_used);
        setErrors(prev => ({ ...prev, serials: serialError || '' }));
        return !serialError;
    };

    const handleAddPart = async () => {
        if (!formData.part_id || !formData.quantity_used || formData.unit_cost <= 0) {
            alert('Please fill in all required fields');
            return;
        }
        if (!validateSerials()) return;

        setLoading(true);
        try {
            const response = await axios.post(
                `${rootUrl}/api/job-cards/${jobCardId}/parts`,
                formData,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data && response.data.success) {
                onPartAdded && onPartAdded();
                handleResetDialog();
            } else {
                alert(response.data?.message || 'Failed to add part');
            }
        } catch (error) {
            console.error('Error adding part:', error);
            alert(error.response?.data?.message || 'Failed to add part');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdatePart = async () => {
        if (!editingPart) return;
        if (!validateSerials()) return;

        setLoading(true);
        try {
            const response = await axios.put(
                `${rootUrl}/api/job-cards/${jobCardId}/parts/${editingPart.id}`,
                formData,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data && response.data.success) {
                onPartAdded && onPartAdded();
                handleResetDialog();
            } else {
                alert(response.data?.message || 'Failed to update part');
            }
        } catch (error) {
            console.error('Error updating part:', error);
            alert(error.response?.data?.message || 'Failed to update part');
        } finally {
            setLoading(false);
        }
    };

    const resetNewPartData = () => {
        setNewPartData({
            part_name: '',
            part_number: '',
            manufacturer: '',
            category: '',
            vat_id: 1,
            cost_excl: 0,
            cost_incl: 0,
            price_excl: 0,
            price_incl: 0,
            quantity: 0
        });
    };

    const handleResetDialog = () => {
        setIsCreatingNewPart(false);
        setErrors({});
        setBanner(null);
        setFormData({
            part_id: '',
            quantity_used: 1,
            unit_cost: 0,
            discount_amount: 0,
            paid_by: 'OFFICE',
            is_sale: false,
            notes: '',
            serials: []
        });
        resetNewPartData();
        onClose();
    };

    return (
        <Dialog open={open} handler={handleResetDialog} size="md" className="max-h-screen overflow-y-auto">
            <DialogHeader className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <div>
                    <Typography variant="h6">
                        {editingPart ? 'Edit Part' : isCreatingNewPart ? 'Create New Part' : 'Add Part'}
                    </Typography>
                </div>
                {!editingPart && (
                    <Button
                        size="sm"
                        variant="text"
                        color="blue"
                        onClick={() => setIsCreatingNewPart(!isCreatingNewPart)}
                        className="text-xs md:text-sm whitespace-nowrap"
                    >
                        {isCreatingNewPart ? 'Select Existing' : 'Create New'}
                    </Button>
                )}
            </DialogHeader>
            <DialogBody className="space-y-3 md:space-y-4">
                {banner?.text && (
                    <div className={banner.type === 'success' ? 'bg-green-50 border border-green-200 rounded p-2 md:p-3' : 'bg-red-50 border border-red-200 rounded p-2 md:p-3'}>
                        <Typography variant="small" className={banner.type === 'success' ? 'text-green-600 text-xs md:text-sm' : 'text-red-600 text-xs md:text-sm'}>
                            {banner.text}
                        </Typography>
                    </div>
                )}
                {errors.submit && (
                    <div className="bg-red-50 border border-red-200 rounded p-2 md:p-3">
                        <Typography variant="small" className="text-red-600 text-xs md:text-sm">
                            {errors.submit}
                        </Typography>
                    </div>
                )}

                {isCreatingNewPart ? (
                    <>
                        <div>
                            <Input
                                label="Part Name *"
                                value={newPartData.part_name}
                                onChange={(e) => {
                                    setNewPartData({ ...newPartData, part_name: e.target.value.toUpperCase() });
                                    if (errors.part_name) setErrors({ ...errors, part_name: '' });
                                }}
                                className="text-xs md:text-sm"
                            />
                            {errors.part_name && <Typography variant="small" className="text-red-500 text-xs mt-1">{errors.part_name}</Typography>}
                        </div>

                        <div>
                            <Input
                                label="Part Number *"
                                value={newPartData.part_number}
                                onChange={(e) => {
                                    setNewPartData({ ...newPartData, part_number: e.target.value.toUpperCase() });
                                    if (errors.part_number) setErrors({ ...errors, part_number: '' });
                                }}
                                className="text-xs md:text-sm"
                            />
                            {errors.part_number && <Typography variant="small" className="text-red-500 text-xs mt-1">{errors.part_number}</Typography>}
                        </div>

                        <div>
                            <Typography variant="small" className="text-gray-700 mb-1">Manufacturer *</Typography>
                            <ReactSelect
                                classNamePrefix="react-select"
                                options={manufacturers.map((mfg) => ({ value: mfg.name, label: mfg.name }))}
                                value={newPartData.manufacturer ? { value: newPartData.manufacturer, label: newPartData.manufacturer } : null}
                                onChange={(opt) => {
                                    const val = opt?.value || '';
                                    setNewPartData({ ...newPartData, manufacturer: val });
                                    if (errors.manufacturer) setErrors({ ...errors, manufacturer: '' });
                                }}
                            />
                            {errors.manufacturer && <Typography variant="small" className="text-red-500 text-xs mt-1">{errors.manufacturer}</Typography>}
                        </div>

                        <div>
                            <Typography variant="small" className="text-gray-700 mb-1">Category *</Typography>
                            <ReactSelect
                                classNamePrefix="react-select"
                                options={categories.map((cat) => ({ value: cat.name, label: cat.name }))}
                                value={newPartData.category ? { value: newPartData.category, label: newPartData.category } : null}
                                onChange={(opt) => {
                                    const val = opt?.value || '';
                                    setNewPartData({ ...newPartData, category: val });
                                    if (errors.category) setErrors({ ...errors, category: '' });
                                }}
                            />
                            {errors.category && <Typography variant="small" className="text-red-500 text-xs mt-1">{errors.category}</Typography>}
                        </div>

                        <div>
                            <Typography variant="small" className="text-gray-700 mb-1">VAT Rate *</Typography>
                            <ReactSelect
                                classNamePrefix="react-select"
                                options={vatRates.map((vat) => ({ value: vat.vat_id.toString(), label: `${vat.vat_name} (${vat.vat_rate}%)` }))}
                                value={{ value: newPartData.vat_id?.toString(), label: `${(vatRates.find(v => Number(v.vat_id) === Number(newPartData.vat_id))?.vat_name) || 'VAT'} (${getVatRateById(newPartData.vat_id)}%)` }}
                                onChange={(opt) => {
                                    const val = opt?.value || '1';
                                    const newVatId = parseInt(val);
                                    const rate = getVatRateById(newVatId);
                                    const costEx = newPartData.cost_excl;
                                    const priceEx = newPartData.price_excl;
                                    setNewPartData({
                                        ...newPartData,
                                        vat_id: newVatId,
                                        cost_incl: costEx > 0 ? parseFloat(computeIncl(costEx, rate)) : 0,
                                        price_incl: priceEx > 0 ? parseFloat(computeIncl(priceEx, rate)) : 0
                                    });
                                    if (errors.vat_id) setErrors({ ...errors, vat_id: '' });
                                }}
                            />
                            {errors.vat_id && <Typography variant="small" className="text-red-500 text-xs mt-1">{errors.vat_id}</Typography>}
                            <Typography variant="small" className="text-gray-600 text-xs mt-1">
                                Current VAT: {getVatRateById(newPartData.vat_id)}%
                            </Typography>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                            <div>
                                <Input
                                    type="number"
                                    label="Cost (excl VAT) *"
                                    value={newPartData.cost_excl}
                                    onChange={(e) => {
                                        const val = parseFloat(e.target.value) || 0;
                                        const rate = getVatRateById(newPartData.vat_id);
                                        setNewPartData({
                                            ...newPartData,
                                            cost_excl: val,
                                            cost_incl: val > 0 ? parseFloat(computeIncl(val, rate)) : 0
                                        });
                                        if (errors.cost_excl) setErrors({ ...errors, cost_excl: '' });
                                    }}
                                    step="0.01"
                                    min={0}
                                    className="text-xs md:text-sm text-right"
                                />
                                {errors.cost_excl && <Typography variant="small" className="text-red-500 text-xs mt-1">{errors.cost_excl}</Typography>}
                            </div>

                            <div>
                                <Input
                                    type="number"
                                    label="Cost (incl VAT) *"
                                    value={newPartData.cost_incl}
                                    onChange={(e) => {
                                        const val = parseFloat(e.target.value) || 0;
                                        const rate = getVatRateById(newPartData.vat_id);
                                        setNewPartData({
                                            ...newPartData,
                                            cost_incl: val,
                                            cost_excl: val > 0 ? parseFloat(computeExcl(val, rate)) : 0
                                        });
                                        if (errors.cost_incl) setErrors({ ...errors, cost_incl: '' });
                                    }}
                                    step="0.01"
                                    min={0}
                                    className="text-xs md:text-sm text-right"
                                />
                                {errors.cost_incl && <Typography variant="small" className="text-red-500 text-xs mt-1">{errors.cost_incl}</Typography>}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                            <div>
                                <Input
                                    type="number"
                                    label="Price (excl VAT) *"
                                    value={newPartData.price_excl}
                                    onChange={(e) => {
                                        const val = parseFloat(e.target.value) || 0;
                                        const rate = getVatRateById(newPartData.vat_id);
                                        setNewPartData({
                                            ...newPartData,
                                            price_excl: val,
                                            price_incl: val > 0 ? parseFloat(computeIncl(val, rate)) : 0
                                        });
                                        if (errors.price_excl) setErrors({ ...errors, price_excl: '' });
                                    }}
                                    step="0.01"
                                    min={0}
                                    className="text-xs md:text-sm text-right"
                                />
                                {errors.price_excl && <Typography variant="small" className="text-red-500 text-xs mt-1">{errors.price_excl}</Typography>}
                            </div>

                            <div>
                                <Input
                                    type="number"
                                    label="Price (incl VAT) *"
                                    value={newPartData.price_incl}
                                    onChange={(e) => {
                                        const val = parseFloat(e.target.value) || 0;
                                        const rate = getVatRateById(newPartData.vat_id);
                                        setNewPartData({
                                            ...newPartData,
                                            price_incl: val,
                                            price_excl: val > 0 ? parseFloat(computeExcl(val, rate)) : 0
                                        });
                                        if (errors.price_incl) setErrors({ ...errors, price_incl: '' });
                                    }}
                                    step="0.01"
                                    min={0}
                                    className="text-xs md:text-sm text-right"
                                />
                                {errors.price_incl && <Typography variant="small" className="text-red-500 text-xs mt-1">{errors.price_incl}</Typography>}
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded p-3 md:p-4">
                            <Typography variant="small" className="font-semibold text-gray-700 text-xs md:text-sm">
                                Initial Quantity: 0 (will be set when recording stock in)
                            </Typography>
                        </div>
                    </>
                ) : (
                    <>
                        <div>
                            <Typography variant="small" className="text-gray-700 mb-1">Part *</Typography>
                            <ReactSelect
                                classNamePrefix="react-select"
                                isDisabled={!!editingPart}
                                options={availableParts.map((part) => ({ value: part.part_id, label: `${part.part_name} - ${part.part_number} (Stock: ${part.quantity || 0})` }))}
                                value={formData.part_id ? { value: formData.part_id, label: `${(availableParts.find(p => p.part_id === formData.part_id)?.part_name) || ''} - ${(availableParts.find(p => p.part_id === formData.part_id)?.part_number) || ''} (Stock: ${(availableParts.find(p => p.part_id === formData.part_id)?.quantity) || 0})` } : null}
                                onChange={(opt) => {
                                    const val = opt?.value || '';
                                    setFormData({ ...formData, part_id: val, discount_amount: 0, serials: [] });
                                    const part = availableParts.find(p => p.part_id === val);
                                    if (part) {
                                        setFormData(prev => ({ ...prev, unit_cost: part.price_incl || 0 }));
                                    }
                                }}
                            />
                        </div>

                        <div>
                            <Input
                                type="number"
                                label="Quantity *"
                                value={formData.quantity_used}
                                onChange={(e) => setFormData({ ...formData, quantity_used: parseInt(e.target.value) || 1 })}
                                min={1}
                                className="text-xs md:text-sm"
                            />
                        </div>

                        {selectedPartSerialsMissing && (
                            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">{serialsMissingMessage}</p>
                        )}
                        {selectedPartIsSerial && !selectedPartSerialsMissing && (
                            <div>
                                <SerialNumbersField
                                    key={editingPart?.id || 'new'}
                                    mode={serviceLocation === 'in_house' ? 'select' : 'enter'}
                                    partId={formData.part_id}
                                    quantity={formData.quantity_used}
                                    value={formData.serials}
                                    extraOptions={editingPart?.serials || []}
                                    onChange={(serials) => setFormData(prev => ({ ...prev, serials }))}
                                    label={serviceLocation === 'in_house' ? 'Serial numbers issued from stock' : 'Serial numbers fitted (external)'}
                                />
                                {errors.serials && (
                                    <Typography variant="small" className="mt-1 text-xs text-red-500">{errors.serials}</Typography>
                                )}
                            </div>
                        )}

                        <div>
                            <Input
                                type="number"
                                label="Unit Cost *"
                                value={formData.unit_cost}
                                onChange={(e) => setFormData({ ...formData, unit_cost: parseFloat(e.target.value) || 0 })}
                                step="0.01"
                                min={0}
                                className="text-xs md:text-sm text-right"
                            />
                        </div>

                        <div>
                            <Input
                                type="number"
                                label="Discount (incl VAT)"
                                value={formData.discount_amount}
                                onChange={(e) => {
                                    const val = parseFloat(e.target.value);
                                    setFormData({ ...formData, discount_amount: isNaN(val) ? 0 : val });
                                }}
                                step="0.01"
                                min={0}
                                className="text-xs md:text-sm text-right"
                            />
                        </div>

                        <Typography variant="small" color="gray" className="text-xs md:text-sm">
                            Total: {formatCurrency(Math.max(0, (Number(formData.quantity_used) || 0) * (Number(formData.unit_cost) || 0) - (Number(formData.discount_amount) || 0)))}
                        </Typography>

                        <div>
                            <Typography variant="small" className="text-gray-700 mb-1">Paid By</Typography>
                            <ReactSelect
                                classNamePrefix="react-select"
                                options={payers.map(p => ({ value: p.code, label: p.name }))}
                                value={payers.find(p => p.code === formData.paid_by) ? { value: formData.paid_by, label: payers.find(p => p.code === formData.paid_by).name } : null}
                                onChange={(opt) => setFormData({ ...formData, paid_by: opt?.value || 'OFFICE' })}
                                isDisabled={payersLoading}
                                placeholder={payersLoading ? "Loading payers..." : "Select who pays"}
                            />
                        </div>

                        <div>
                            <Input
                                label="Notes"
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                className="text-xs md:text-sm"
                            />
                        </div>
                    </>
                )}
            </DialogBody>
            <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 md:gap-3">
                <Button
                    variant="text"
                    color="red"
                    onClick={handleResetDialog}
                    className="w-full sm:w-auto text-xs md:text-sm"
                >
                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                    Cancel
                </Button>
                <Button
                    color="green"
                    onClick={isCreatingNewPart ? handleCreateNewPart : (editingPart ? handleUpdatePart : handleAddPart)}
                    disabled={loading}
                    className="w-full sm:w-auto text-xs md:text-sm"
                >
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    {loading ? 'Saving...' : 'Save'}
                </Button>
            </DialogFooter>
        </Dialog>
    );
};

export default PartDialog;
